<template>
  <div class="products-listing-container">
    <SortAndFilterContainer
      :entity-type="entityType"
      :sort-by-options="sortByOptions"
      @sort-change="onSortChange"
    />
    <SidebarContainer
      class="is-hidden-mobile"
      :entity-type="entityType"
      :entity-value="entityValue"
      :param-top-category="paramTopCategory"
      :param-category="paramCategory"
      :param-sub-category="paramSubCategory"
    />
    <div class="products-container" :class="{ empty: products.length === 0 }">
      <slot name="product-recommendations" />

      <template v-if="!isProductListRefreshPending && products.length === 0">
        <span>{{ $t('products.empty') }}</span>
      </template>
      <Loader v-else :loading="isProductListRefreshPending">
        <div class="page-configuration is-hidden-mobile">
          <SelectBox
            :label="`${$t('products.sort')}:`"
            :options="sortByOptions"
            @change="onSortChange"
          />
          <SelectBox
            :label="`${$t('products.view')}:`"
            :options="viewByOptions"
            @change="onPageSizeChange"
          />
          <Pagination
            class="is-hidden-touch"
            v-bind="paginationProps"
            :is-compact="true"
            @changed="onPageChange"
          />
        </div>
        <ProductsGrid :entity-type="entityType" :products="products" />
        <Pagination
          class="bottom-pagination"
          v-bind="paginationProps"
          :is-compact="false"
          @changed="onPageChange"
        />
      </Loader>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SelectBox from '@sephora-asia/inputs-select-box/SelectBox.vue'
import Pagination from '@sephora-asia/core-pagination/Pagination.vue'
import Loader from '@sephora-asia/core-loader/Loader.vue'
import isEmpty from 'lodash/isEmpty'
import i18n from '../mixins/i18n'
import queryString from '../mixins/queryString'
import productFiltersHelper from '../mixins/productFiltersHelper'
import {
  LAUNCH_CONFIG_PLATFORM,
  LaunchStatus
} from '../constants/productLaunchConstants'
import ProductsGrid from './ProductsGrid.vue'
import SidebarContainer from './SidebarContainer.vue'
import SortAndFilterContainer from './SortAndFilterContainer.vue'

export default {
  name: 'ProductsListingContainer',
  components: {
    Loader,
    Pagination,
    ProductsGrid,
    SelectBox,
    SidebarContainer,
    SortAndFilterContainer
  },
  mixins: [i18n, queryString, productFiltersHelper],
  props: {
    /**
     * Page entity type
     */
    entityType: {
      type: String,
      required: true
    },
    /**
     * Page entity value
     */
    entityValue: {
      type: String,
      default: ''
    },
    /**
     * This is defined in the route. Prefixed with 'param' to not get confused
     */
    paramTopCategory: {
      type: String,
      required: true
    },
    /**
     * This is defined in the route. Prefixed with 'param' to not get confused
     */
    paramCategory: {
      type: String,
      required: true
    },
    /**
     * This is defined in the route. Prefixed with 'param' to not get confused
     */
    paramSubCategory: {
      type: String,
      required: true
    }
  },
  data() {
    const sortByOptionsData = [
      {
        value: 'sales',
        label: this.$t('products.featured'),
        isSelected: true
      },
      {
        value: 'published_at',
        label: this.$t('products.newest'),
        isSelected: false
      },
      {
        value: 'rating',
        label: this.$t('products.topRated'),
        isSelected: false
      },
      {
        value: 'price',
        label: this.$t('products.priceLowToHigh'),
        isSelected: false
      },
      {
        value: '-price',
        label: this.$t('products.priceHighToLow'),
        isSelected: false
      }
    ]

    if (this.entityType === 'search') {
      sortByOptionsData[0].isSelected = false

      sortByOptionsData.unshift({
        value: 'relevance',
        label: this.$t('products.relevance'),
        isSelected: true
      })
    }

    const DEFAULT_SORT = sortByOptionsData[0].value

    const viewByOptionsData = [
      {
        value: 36,
        label: 36,
        isSelected: true
      },
      {
        value: 60,
        label: 60,
        isSelected: false
      },
      {
        value: 120,
        label: 120,
        isSelected: false
      }
    ]
    const DEFAULT_PAGE_SIZE = viewByOptionsData[0].value

    return {
      // Setting initial value to true so that the no products message does not flicker
      // everytime before the loader appears
      isSearchResultTracked: false,
      isProductListRefreshPending: true,
      toRefreshProductList: true,
      sortByOptionsData,
      viewByOptionsData,
      DEFAULT_PAGE_SIZE,
      DEFAULT_SORT
    }
  },
  async fetch() {
    // reset page type filters
    this.$store.commit('listingPageFilters/setListingPageTypeFilters', [])

    if (this.entityType !== 'search') await this._fetchFilters()

    await this._fetchProducts()
    this._updateStoreFilterParams()
  },
  head() {
    const origin = globalThis.location?.origin
    const itemListElement = this.products.map((item, i) => ({
      '@type': 'ListItem',
      name: item.productName,
      image: item.featuredVariant.imageUrl,
      url: origin + item.pdpUrl,
      position: i + 1
    }))
    return {
      script: [
        {
          json: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement
          },
          type: 'application/ld+json'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      meta: 'listingPageProducts/meta',
      products: 'listingPageProducts/products',
      userLoggedIn: 'user/loggedIn',
      userLoyaltyTier: 'user/loyaltyTier',
      brands: 'listingPageFilters/brandFilters',
      otherFilters: 'listingPageFilters/otherFilters',
      filterTypes: 'listingPageFilters/typeFilters'
    }),
    sortByOptions() {
      return this.sortByOptionsData
    },
    viewByOptions() {
      return this.viewByOptionsData
    },
    paginationProps() {
      return {
        currentPage: this.meta.currentPage || 1,
        currentUrl: this.$route.fullPath,
        totalPages: this.meta.totalPages || 1
      }
    }
  },
  watch: {
    '$route.query': '_fetchProducts'
  },

  mounted() {
    // this is to track the event upon page load
    // as this.$analytics is not available in server side
    this._trackEvent()
  },

  async created() {
    // This is required in case user mess with the query param to make it invalid
    if (process.client) {
      let query
      query = await this._getCorrectQuery({ ...this.$route.query })
      query = await this._checkWaitlist(query)
      query = await this._checkWishlist(query)
      query = await this._checkRemindMeSubscribedVariant(query)

      if (Object.keys(this.$route.query).length !== Object.keys(query).length) {
        this.toRefreshProductList = false

        await this.$router.replace({ query })

        this.toRefreshProductList = true
      }
    }
  },
  destroyed() {
    this.$store.dispatch('listingPageProducts/clear')
  },
  methods: {
    onPageSizeChange(event) {
      const { view: currentPageSize } = this.$route.query
      if (event.target.value === currentPageSize) return
      const pageSize = Number(event.target.value)
      const pageParams = { page: undefined }
      if (pageSize === this.DEFAULT_PAGE_SIZE) {
        pageParams.view = undefined
      } else {
        pageParams.view = pageSize
      }
      this.sortAndBuildUrlQuery(pageParams)
    },
    onPageChange(pageNumber) {
      const { currentPage } = this.paginationProps
      if (pageNumber === currentPage) return
      this.sortAndBuildUrlQuery({
        page: pageNumber <= 1 ? undefined : pageNumber
      })
    },
    onSortChange(event) {
      const { sort: currentSort } = this.$route.query
      if (event.target.value === currentSort) return
      const sort = event.target.value
      const pageParams = { page: undefined }
      if (sort === this.DEFAULT_SORT || sort === '') {
        pageParams.sort = undefined
      } else {
        pageParams.sort = sort
      }
      this._trackSortingEvent(sort)
      this.sortAndBuildUrlQuery(pageParams)
    },
    _trackSortingEvent(value) {
      this.$analytics.trackEvent('click', {
        params: {
          event_category: 'Filters',
          event_action: 'Sort By',
          event_label: value
        }
      })
    },
    async _fetchProducts() {
      if (!this.toRefreshProductList) return

      this.isProductListRefreshPending = true

      globalThis.scrollTo?.({
        top: 0,
        behavior: 'smooth' // smooth doesn't work for safari
      })

      const {
        page: pageNumber = 1,
        view: pageSize = this.DEFAULT_PAGE_SIZE,
        sort = this.DEFAULT_SORT,
        'min-price': minPrice = null,
        'max-price': maxPrice = null,
        q: query = null
      } = this._getCorrectQuery({ ...this.$route.query })

      this._updateViewByOptionsDefault(pageSize)
      this._updateSortByOptionsDefault(sort)

      let params = {}
      params['page[number]'] = Number(pageNumber)
      params['page[size]'] = pageSize
      params.sort = sort
      if (minPrice) params['where[min_price]'] = minPrice * 100
      if (maxPrice) params['where[max_price]'] = maxPrice * 100
      const whereParams = this.buildWhereParams()
      if (!isEmpty(whereParams)) params = { ...params, ...whereParams }
      if (this.entityType === 'search') {
        params.query = query
        await this.$store.dispatch('listingPageSearch/fetchProducts', {
          params,
          language: this.$i18n.locale,
          path: this.$route.path
        })
      } else {
        params[`filter[${this.entityType}]`] = this.entityValue
        await this.$store.dispatch('listingPageProducts/fetchProducts', params)
      }
      this.isProductListRefreshPending = false
      // this is to track the event upon client call
      this._trackEvent()
    },
    _trackEvent() {
      const { sort = this.DEFAULT_SORT, q: query = null } =
        this._getCorrectQuery({ ...this.$route.query })

      if (this.entityType === 'search') {
        this._trackProductSearch({ query })
      } else {
        this._trackProductFetch({ sort })
      }
    },
    _trackProductSearch(queryParams) {
      if (this.isSearchResultTracked) return

      let event = 'search_results_no_results_impression_ssr'
      const data = {
        params: {
          search_term: queryParams.query
        }
      }

      if (this.meta.totalProducts > 0) {
        event = 'search_results_total_ssr'
        data.params.search_type = this.meta.totalProducts
      }

      this.$analytics?.trackEvent(event, data)
      if (process.client) this.isSearchResultTracked = true
    },
    _trackProductFetch(queryParams) {
      const data = {
        params: {
          event_category: 'List',
          event_action: 'Refreshed',
          event_label: this.entityType,
          cd8: this.paramTopCategory || 'none',
          cd9: this.paramCategory || 'none',
          cd10: this.paramSubCategory || 'none',
          cd11: this.entityType === 'brand' ? this.entityValue : 'none',
          cd17: this.meta.totalProducts,
          cd18: this.meta.perPage,
          cd19: queryParams.sort,
          cd20: this.meta.currentPage,
          new_product_list: this.products.map((product, index) => ({
            name: product.productName,
            sku: product.id,
            brand: product.brandName,
            position: index + 1,
            out_of_stock: product.soldOut
          }))
        }
      }

      this.$analytics?.trackEvent('listing_refresh', data)
    },
    _updateViewByOptionsDefault(pageSize) {
      this.viewByOptionsData = this.viewByOptionsData.map((opt) => ({
        ...opt,
        isSelected: opt.value === Number(pageSize)
      }))
    },
    _updateSortByOptionsDefault(sort) {
      this.sortByOptionsData = this.sortByOptionsData.map((opt) => ({
        ...opt,
        isSelected: opt.value === sort
      }))
    },
    /**
     * To validate the query params
     * If query params is invalid state, return the correct query
     *
     * @param {object} query - query param object
     * @returns {object|undefined} - if query is valid, will return undefined. Else, will return valid query param
     */
    _getCorrectQuery(query) {
      const { view: pageSize = this.DEFAULT_PAGE_SIZE } = query
      const { sort = this.DEFAULT_SORT } = query
      const { 'min-price': minPrice = 0 } = query
      const { 'max-price': maxPrice = 0 } = query
      const newQuery = { ...query }

      if (
        // eslint-disable-next-line eqeqeq
        pageSize == this.DEFAULT_PAGE_SIZE ||
        !this._isValidPageSize(pageSize)
      ) {
        newQuery.view = undefined
      }

      if (sort === this.DEFAULT_SORT || !this._isValidSort(sort)) {
        newQuery.sort = undefined
      }

      if (minPrice <= 0) newQuery['min-price'] = undefined
      if (maxPrice <= 0) newQuery['max-price'] = undefined

      if (
        !this._isValidSort(sort) ||
        !this._isValidPageSize(pageSize) ||
        // eslint-disable-next-line eqeqeq
        newQuery.page == 1
      ) {
        delete newQuery.page
      }

      for (const key in newQuery) {
        if (newQuery[key] === null || newQuery[key] === undefined) {
          delete newQuery[key]
        }
      }

      return newQuery
    },
    async _checkWaitlist(query) {
      if (this.userLoggedIn) {
        // fetch waitlist
        await this.$store.dispatch('waitlist/fetchWaitlist')
        // add variant to waitlist if waitlistId exists in params
        if (query.waitlistId) {
          try {
            await this.$store.dispatch(
              'waitlist/addToWaitlist',
              query.waitlistId
            )
          } catch (error) {
            this.$notify({
              text: error,
              type: 'error'
            })
          }
          delete query.waitlistId
        }
      }

      return query
    },
    async _checkWishlist(query) {
      if (!this.userLoggedIn) {
        return query
      }

      // fetch wishlist
      await this.$store.dispatch('wishlist/fetchWishlist')
      // add variant to wishlist if wishlistId exists in params
      if (query.wishlistId) {
        try {
          await this.$store.dispatch('wishlist/addToWishlist', query.wishlistId)
          const { productName } =
            this._getProductsByVariantId(query.wishlistId) || {}
          this.$notify({
            text: this.$t('wishlist.added', {
              productName
            })
          })
        } catch (error) {
          this.$notify({
            text: error,
            type: 'error'
          })
        }
        delete query.wishlistId
      }

      return query
    },

    isEarlyAccessQualifiedUser(product) {
      return (
        this.userLoggedIn &&
        this.userLoyaltyTier &&
        product?.featuredVariant?.launchConfig?.prelaunch?.tiers?.includes(
          this.userLoyaltyTier
        )
      )
    },
    isAppExclusive(product) {
      if (product.featuredVariant.launchStatus === LaunchStatus.Prelaunch) {
        return (
          !this.isValidPlatform(product) &&
          this.isEarlyAccessQualifiedUser(product)
        )
      }
      return false
    },
    isValidPlatform(product) {
      const { launchConfig } = product.featuredVariant
      return launchConfig?.prelaunch?.platforms?.some((platform) =>
        LAUNCH_CONFIG_PLATFORM.includes(platform)
      )
    },
    showRemindMe(product) {
      const { launchStatus, soldOut } = product.featuredVariant

      if (soldOut && this.isAppExclusive(product)) return false

      if (launchStatus === LaunchStatus.Preview) return true

      return launchStatus === LaunchStatus.Prelaunch
        ? !(
            this.isEarlyAccessQualifiedUser(product) &&
            this.isValidPlatform(product)
          )
        : false
    },
    async _checkRemindMeSubscribedVariant(query) {
      if (!this.userLoggedIn) {
        return query
      }

      await this.$store.dispatch(
        'earlyAccessNotification/fetchNotificationSubscribedVariantIds'
      )

      if (query?.subscribedVariantId) {
        const product =
          this._getProductsByVariantId(query.subscribedVariantId) || {}

        if (!isEmpty(product) && this.showRemindMe(product)) {
          const type = this.isEarlyAccessQualifiedUser(product)
            ? 'prelaunch'
            : 'launch'

          try {
            await this.$store.dispatch(
              'earlyAccessNotification/subscribeToNotification',
              {
                eventType: `products_${type}`,
                eventName:
                  product?.featuredVariant?.launchConfig[type]?.name || '',
                variantId: product?.featuredVariant.id
              }
            )
            this.$notify({
              text: this.$t('products.earlyAccess.successfullyNotified')
            })
          } catch (error) {}
        }
        delete query.subscribedVariantId
      }

      return query
    },
    _getProductsByVariantId(variantId) {
      return this.products.find(
        (product) => product.featuredVariant.id === variantId
      )
    },
    _isValidPageSize(pageSize) {
      // eslint-disable-next-line eqeqeq
      return this.viewByOptionsData.some((obj) => obj.value == pageSize)
    },
    _isValidSort(sort) {
      return this.sortByOptionsData.some((obj) => obj.value === sort)
    },
    async _fetchFilters() {
      await this.$store.dispatch('listingPageFilters/fetchFilters', {
        entityType: this.entityType,
        entityValue: this.entityValue
      })
    },
    _updateStoreFilterParams() {
      // Initialise store filter params with URL query values and API filter keys
      // This makes the state reactive and trackable with v-model
      // ref: https://github.com/vuejs/vue/issues/3732#issuecomment-249212571
      // https://github.com/vuejs/vue/issues/5932#issuecomment-334040085
      const routeQuery = this.$route.query || {}
      this.updateStoreBrandFilterParams(routeQuery, this.brands)
      this.updateStoreTypeFilterParams(routeQuery, this.filterTypes)
      this.updateStoreOtherFilterParams(routeQuery, this.otherFilters)
    },
    buildBrandFilterParams() {
      const brandParams = {}
      const brandSlugUrlStrings = this.$route.query.brands
      switch (this.entityType) {
        case 'search':
          brandParams['where[brand]'] = brandSlugUrlStrings
          break
        case 'brand':
          brandParams['filter[brand]'] = this.entityValue
          break
        default: {
          const brandSlugUrls = brandSlugUrlStrings?.split(',')
          if (brandSlugUrls?.length > 0) {
            const brands = []
            for (const slugUrl of brandSlugUrls) {
              const brand = this.brands.find((b) => b.slugUrl === slugUrl)
              if (brand) brands.push(brand.slugUrl)
            }
            if (brands) brandParams['where[brand]'] = brands.join(',')
          }
        }
      }
      return brandParams
    },
    buildCategoryFilterParams() {
      if (this.entityType === 'category') return {}

      const categoryFilterParams = {}
      categoryFilterParams['where[category]'] = [
        this.paramTopCategory,
        this.paramCategory,
        this.paramSubCategory
      ]
        .filter((cat) => !!cat)
        .join('/')

      return categoryFilterParams
    },
    buildFilterParams() {
      const filterParams = {}
      for (const [filterType, filterValues] of Object.entries(
        this.$route.query
      )) {
        const selectedFilterType = this.filterTypes.find(
          (ft) => ft.slugUrl === filterType
        )
        if (selectedFilterType) {
          const selectedFilterValueIds = []

          filterValues.split(',').forEach((slugUrl) => {
            const filterValue = selectedFilterType.filterValues.find(
              (fv) => fv.slugUrl === slugUrl
            )
            if (filterValue) selectedFilterValueIds.push(filterValue.id)
          })
          if (selectedFilterValueIds) {
            filterParams[`where[filter_type][${selectedFilterType.id}]`] =
              selectedFilterValueIds.join(',')
          }
        }
      }
      return filterParams
    },
    buildOthersParams() {
      const othersSlugUrls = this.$route.query.others
      if (othersSlugUrls?.length === undefined) return {}

      if (this.entityType === 'search') {
        return { 'where[others]': othersSlugUrls }
      } else {
        const others = []
        for (const slugUrl of othersSlugUrls?.split(',')) {
          const other = this.otherFilters.find((o) => o.slugUrl === slugUrl)
          if (other) others.push(other.slugUrl)
        }
        if (others.length) return { 'where[others]': others.join(',') }

        return {}
      }
    },
    buildWhereParams() {
      // mapping slugUrl to ID before sending to api
      return {
        ...this.buildBrandFilterParams(),
        ...this.buildCategoryFilterParams(),
        ...this.buildFilterParams(),
        ...this.buildOthersParams()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.products-listing-container {
  padding-top: 10px;
  --col-gap: 24px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  column-gap: var(--col-gap);

  @include tablet {
    grid-template-columns: 235px minmax(0, 1fr);
  }

  @include desktop {
    grid-template-columns: 270px minmax(0, 1fr);
    --col-gap: 58px;
  }

  @include widescreen {
    --col-gap: 24px;
  }

  .empty {
    text-align: center;
    font-weight: bold;
  }

  .page-configuration,
  .bottom-pagination {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
    margin-bottom: 48px;
  }

  .page-configuration {
    gap: 32px;

    /deep/ .select-box-container .label {
      text-transform: uppercase;
    }
  }

  .bottom-pagination {
    margin-top: 48px;
  }
}

::v-deep .loader-spinner {
  margin: 20px 0;
}
</style>
